import { ApiResponse } from 'apisauce';
import { ErrorInfo } from '../shared/interfaces/error-info.interface';
import { ResponseModel } from '../shared/interfaces/response-model.interface';
import {
  BulkUpdate,
  BulkUpdateOutcomeRecord,
  ChannelManagerSyncStatus
} from '../shared/models/unit-types.model';
import api from '../shared/services/utils/api/api.service';
import errorHelper from '../shared/services/utils/api/error-helper.service';

const MODULE_NAME = 'bulk-actions-list';

export interface BulkActionsPage {
  bulkUpdates: BulkUpdate[];
  count: number;
}

/**
 * Reads the bulk-update history, newest first.
 *
 * `offset` is 1-based, matching the rest of the admin APIs. The endpoint wraps its
 * payload twice — `{ data: { data: [...], count } }` — which is why the unwrapping
 * below looks redundant but is not.
 */
export function getBulkActions(
  offset: number,
  limit: number
): Promise<BulkActionsPage | ErrorInfo> {
  return api
    .getAllBulkUpdateUnitTypeData({ offset, limit })
    .then((response: ApiResponse<ResponseModel<BulkUpdate[]>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response as any;
        if (data && data.data && data.data.data) {
          return {
            bulkUpdates: (data.data.data || []) as BulkUpdate[],
            count: Number(data.data.count) || 0
          };
        }

        return errorHelper(
          0,
          '',
          MODULE_NAME,
          (api.getAllBulkUpdateUnitTypeData as any).displayName
        );
      }

      return errorHelper(
        Number(response.status),
        response.data && response.data.errorCode
          ? String(response.data.errorCode || '')
          : response.problem,
        MODULE_NAME,
        (api.getAllBulkUpdateUnitTypeData as any).displayName
      );
    });
}

/**
 * Flattens a stored bulk-update record into one row per unit, so a run can be read
 * as "which units failed, and on which side".
 *
 * Records written before per-unit channel-manager tracking existed carry no
 * `channelManagerStatus`. Those come back with `rentalsUnited` undefined, which the
 * UI shows as "not tracked" — guessing 'ok' would invent a success that was never
 * observed, and guessing 'failed' would invent an incident.
 */
export function toOutcomeRecords(bulkUpdate: BulkUpdate): BulkUpdateOutcomeRecord[] {
  return [
    ...(bulkUpdate?.successRecords || []).map((record) => ({
      unitId: record.unitId,
      unitName: record.unitName,
      birdnest: 'ok' as const,
      rentalsUnited: record.channelManagerStatus as ChannelManagerSyncStatus,
      errorMessage: record.channelManagerError
    })),
    ...(bulkUpdate?.errorRecords || []).map((record) => ({
      unitId: record.unitId,
      unitName: record.unitName,
      birdnest: 'failed' as const,
      rentalsUnited: record.channelManagerStatus as ChannelManagerSyncStatus,
      errorMessage: record.errorMessage
    }))
  ];
}

/** Machine codes recorded against a run, mapped to what the team calls them. */
const ACTION_TYPE_LABELS: Record<string, string> = {
  dynamicPrices: 'Dynamic Prices',
  unitPrice: 'Unit Price',
  ratePlans: 'Rate Plans',
  fees: 'Fees',
  stayDuration: 'Stay Duration',
  unitDetails: 'Unit Details'
};

/**
 * Human label for what a run changed.
 *
 * Runs recorded before action types existed have no `actionType`, and are reported
 * as not recorded rather than being attributed to a guess.
 */
export function formatActionType(actionType?: string): string | undefined {
  const codes = (actionType || '')
    .split(',')
    .map((code) => code.trim())
    .filter(Boolean);

  if (codes.length === 0) return undefined;

  return codes.map((code) => ACTION_TYPE_LABELS[code] || code).join(' + ');
}
